import useSWR from 'swr';
import { FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';

import { TKeyValuePair, IRepository, ISchemaField } from '@/types';
import { TViewMode } from '@/components/DataFrame/types';

import {
  fetchRemoteDataframe,
  fetchRemoteDataframeDiff,
  addRowToRemoteDataframe,
  deleteRowFromRemoteDataframe,
  updateRemoteDataframeRow,
  revertRowChangesFromRemoteDataframe,
  getResultsDownloadUrl,
} from '@/lib/workspaces/tabularApi';

import { addColumn, deleteColumn, updateColumn, restoreColumn } from '@/lib/workspaces/data_frames/columns';

import { IDataFrameResponse } from '@/lib/dataFrameTypes';
import { TDFRequestQueryParams } from '@/lib/dataFrameApi';

import { revalidateStagedFiles } from '@/hooks/useStagedFiles';

import { SYSTEM_ROW_ID_FIELD_NAME } from '@/components/DataFrame/consts';

interface useRemoteDataframeParams {
  repository: IRepository;
  branch: string;
  path: string;
  query: TDFRequestQueryParams;
  workspaceId: string;
  initialData?: IDataFrameResponse | undefined;
  mode: TViewMode;
}

const getRemoteQueryKey = ({ query }: { query: TDFRequestQueryParams }) => {
  const queryElements = Object.entries(query).sort((a, b) => a[0].localeCompare(b[0]));
  return queryElements.map(([key, value]) => `${key}|${value}`);
};

const useRemoteDataframe = ({
  repository,
  branch,
  path,
  query,
  workspaceId,
  initialData,
  mode,
}: useRemoteDataframeParams) => {
  if (initialData && ['view', 'sample', 'compare'].includes(mode)) {
    return { isLoading: false, isValidating: false, error: undefined, data: initialData, revalidate: () => {} };
  }

  const queryKey = getRemoteQueryKey({ query });
  const { namespace, name: repositoryName } = repository;
  const fetchKey = ['remote_data_frame', namespace, repositoryName, branch, workspaceId, path, mode, ...queryKey];

  const fetcher = async () => {
    if (mode === 'remote_diff') {
      return fetchRemoteDataframeDiff({ repository, path, query, workspaceId });
    }
    return fetchRemoteDataframe({ repository, path, query, workspaceId });
  };

  const {
    data,
    mutate,
    error: requestError,
    isLoading,
    isValidating,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useSWR(fetchKey, () => fetcher(), {
    fallbackData: initialData,
    keepPreviousData: true,
    focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
  });

  const afterUpdate = () => {
    if (!workspaceId) return;
    revalidateStagedFiles({ namespace, repositoryName, workspaceId });
  };

  const addDfRow = async (): Promise<TKeyValuePair> => {
    const response = await addRowToRemoteDataframe({ repository, path, workspaceId });
    if (response.error) {
      console.error('addDfRow > error', response.error);
    }
    const addedRow = response.data_frame.view.data[0];
    mutate();
    afterUpdate();
    return addedRow;
  };

  const removeDfRow = async (rowId: string): Promise<boolean> => {
    const response = await deleteRowFromRemoteDataframe({ rowId, repository, path, workspaceId });
    // console.log('removeDfRow > response', response);
    if (response.status === 'success') {
      mutate();
      afterUpdate();
      return true;
    }
    if (response.error) {
      console.error('removeDfRow > error', response.error);
    }
    return false;
  };

  const updateDfRow = async (changedRow: TKeyValuePair): Promise<boolean> => {
    const rowId = changedRow[SYSTEM_ROW_ID_FIELD_NAME];
    const response = await updateRemoteDataframeRow({
      rowId,
      repository,
      path,
      workspaceId,
      rowData: changedRow,
    });

    if (response.status === 'success') {
      mutate();
      afterUpdate();
      return true;
    }
    if (response.error) {
      console.error('updateDfRow > error', response.error);
    }
    return false;
  };

  const restoreDfRow = async (rowId: string): Promise<boolean> => {
    const response = await revertRowChangesFromRemoteDataframe({ rowId, repository, path, workspaceId });

    if (response.status === 'success') {
      mutate();
      afterUpdate();
      return true;
    }
    return false;
  };

  const addDfColumn = async (column: ISchemaField): Promise<boolean> => {
    const response = await addColumn({ repository, path, workspaceId, column });
    if (response.error) {
      console.error('addDfColumn > error', response.error);
      return false;
    }
    mutate();
    afterUpdate();
    return true;
  };

  const removeDfColumn = async (columnName: string): Promise<boolean> => {
    const response = await deleteColumn({ repository, path, workspaceId, columnName });
    if (response.error) {
      console.error('removeDfColumn > error', response.error);
      return false;
    }
    mutate();
    afterUpdate();
    return true;
  };

  const updateDfColumn = async (
    columnName: string,
    updatedColumn: ISchemaField,
    renderFunc: string,
    codeLanguage?: string,
  ): Promise<boolean> => {
    const response = await updateColumn({
      repository,
      path,
      workspaceId,
      columnName,
      updatedColumn,
      renderFunc,
      codeLanguage,
    });
    if (response.error) {
      console.error('updateDfColumn > error', response.error);
      return false;
    }
    mutate();
    afterUpdate();
    return true;
  };

  const restoreDfColumn = async (currentColumnName: string): Promise<boolean> => {
    const response = await restoreColumn({ repository, path, workspaceId, currentColumnName });
    if (response.error) {
      console.error('restoreDfColumn > error', response.error);
      return false;
    }
    mutate();
    afterUpdate();
    return true;
  };

  const revalidate = () => {
    mutate();
  };

  const resultsDownloadUrl = (fileExtension: string = 'csv') => {
    const targetUrl = getResultsDownloadUrl({ repository, path, query, workspaceId, fileExtension });
    return targetUrl;
  };

  return {
    isLoading,
    isValidating,
    error: requestError,
    data,
    resultsDownloadUrl,
    addDfRow,
    removeDfRow,
    updateDfRow,
    restoreDfRow,
    addDfColumn,
    removeDfColumn,
    updateDfColumn,
    restoreDfColumn,
    revalidate,
  };
};

export default useRemoteDataframe;
